/**
 * lib/views/javascripts/proto/plugins/plugins.js — the Plugins window (ADR
 * 0069 5a-2, Addendum A: one place, one question, one card;
 * lib/views/htmls/proto/plugins.ejs).
 *
 * One window from the rail foot's Plugins, three tabs — Plugins · Skills ·
 * Bots. Plugins: yours first, then everything you can add; each plugin's page
 * asks the ONE question and saves the answer; a blocked or not-yet-built
 * plugin says why; an operator blocks or unblocks with a reason, answered in
 * place (no native dialog). Skills: the agent's own palette. Bots: the
 * place's principal agents (with your tray switches when the host has a
 * tray) and your worker agents. And the one card in the conversation: the
 * chat hands every streamed "card" here (window.__m33ChatCard) — Allow once ·
 * Always allow · Deny, or a new Google file shown inline — kept with the
 * conversation in this browser and put back after its reply on reload.
 *
 * The data is Proto's (/api/plugins, /api/effector/approvals) and the host's
 * palette, workers and tray routes; this module only draws it. Appearance is
 * packages/@mach33/ui/static/plugins.css (rule 10a). Ported from the client
 * lib/proto/plugins-view.js used to serialize into the page (ADR 0082 P3): the
 * same code, with page globals reached through window.
 *
 * Contents: view
 * Ontology: Code object M33C-1104 (immutable ref — assigned once, never edit)
 * Team: J. Brant Arseneau & the Mach33 Agents.
 */

// ── Mount ────────────────────────────────────────────────────────────────
// cfg: { agent, bots: [{ name, role, place?, member?, on?, live? }], tray?:
// { route, rowSelector, note? }, categories: { id: words } } — boot data from
// lib/proto/plugins-view.js. document and window are parameters so a test can
// mount on jsdom.
export function mountPlugins(cfg, { document = globalThis.document, window = globalThis.window } = {}) {
  var root = document.getElementById('m33-pl'), body = document.getElementById('m33-pl-body');
  var q = document.getElementById('m33-pl-q'), h = document.getElementById('m33-pl-h');
  if (!root || !body) return;
  var data = null, tab = 'plugins', open = null, asking = null;
  var esc = function (s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); };
  function getJSON(u) {
    return window.fetch(u, { credentials: 'same-origin', cache: 'no-store' }).then(function (r) {
      return r.json().then(function (d) { if (!r.ok || d.ok === false) throw new Error((d && (d.message || d.error)) || ('HTTP ' + r.status)); return d; });
    });
  }
  function postJSON(u, b) {
    return window.fetch(u, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(b || {}) })
      .then(function (r) { return r.json().then(function (d) { if (!r.ok || !d.ok) throw new Error((d && (d.message || d.error)) || ('HTTP ' + r.status)); return d; }); });
  }
  function fail(where, e) {
    console.error('[plugins-view] ' + where + ':', e && e.message);
    body.innerHTML = '<div class="m33-pl-empty m33-pl-err">Could not ' + esc(where) + ': ' + esc(e && e.message) + '</div>';
  }
  // A plugin's state, in the words people read.
  function stateOf(p) {
    if (p.availability === 'blocked') return { cls: 'blocked', words: 'Blocked' };
    if (p.availability === 'once-built') return { cls: 'soon', words: 'Not yet' };
    // Linked, not Connected, until the agent can call its tools (served) — the pill must not promise more than works (rule 18c).
    if (p.yours && p.yours.via === 'workspace' && !p.served) return { cls: 'wait', words: 'Linked' };
    if (p.yours && p.yours.status === 'connected') return { cls: 'on', words: 'Connected' };
    if (p.yours) return { cls: 'wait', words: 'Needs sign-in' };
    return { cls: 'add', words: 'Add' };
  }
  // The vendor's own mark when the catalog has it; its initial otherwise.
  function tile(p, lg) {
    var cls = 'm33-pl-tile' + (lg ? ' m33-pl-tile-lg' : '');
    return p.logo ? '<span class="' + cls + ' m33-pl-tile-logo" aria-hidden="true"><img src="' + esc(p.logo) + '" alt="" loading="lazy"></span>'
      : '<span class="' + cls + '" aria-hidden="true">' + esc(p.name.charAt(0)) + '</span>';
  }
  function card(p) {
    var st = stateOf(p);
    return '<button type="button" class="m33-pl-card" data-id="' + esc(p.id) + '">' + tile(p) +
      '<span class="m33-pl-meta"><span class="m33-pl-name">' + esc(p.name) + '</span><span class="m33-pl-what">' + esc(p.what) + '</span></span>' +
      '<span class="m33-pl-state m33-pl-st-' + st.cls + '">' + esc(st.words) + '</span></button>';
  }
  function wire(sel, fn) { var els = body.querySelectorAll(sel); for (var i = 0; i < els.length; i++) els[i].addEventListener('click', fn); }

  // ── Plugins: yours, then everything you can add ──
  function drawList() {
    h.textContent = 'Plugins'; q.hidden = false;
    var v = String(q.value || '').trim().toLowerCase();
    var ps = data.plugins.filter(function (p) { return !v || (p.name + ' ' + p.what).toLowerCase().indexOf(v) >= 0; });
    var yours = ps.filter(function (p) { return p.yours; });
    var more = ps.filter(function (p) { return !p.yours; });
    var groups = {}, order = [];
    more.forEach(function (p) { var c = p.category || 'other'; if (!groups[c]) { groups[c] = []; order.push(c); } groups[c].push(p); });
    var html = '<p class="m33-pl-lead">Plugins let ' + esc(cfg.agent) + ' work in the tools you use. Each one asks one question: what may it do?</p>';
    html += '<div class="m33-pl-sub">Yours</div>' + (yours.length ? '<div class="m33-pl-grid">' + yours.map(card).join('') + '</div>'
      : '<div class="m33-pl-empty">None yet. Pick one below.</div>');
    order.forEach(function (c) {
      html += '<div class="m33-pl-sub">' + esc(cfg.categories[c] || c) + '</div><div class="m33-pl-grid">' + groups[c].map(card).join('') + '</div>';
    });
    if (!ps.length) html += '<div class="m33-pl-empty">No plugin matches “' + esc(v) + '”.</div>';
    body.innerHTML = html;
    wire('.m33-pl-card', function () { open = this.getAttribute('data-id'); asking = null; draw(); });
  }

  // ── One plugin: the one question ──
  function drawPlugin(p) {
    h.textContent = p.name; q.hidden = true;
    var html = '<button type="button" class="m33-pl-back" id="m33-pl-back">‹ All plugins</button>' +
      '<div class="m33-pl-page-h">' + tile(p, true) +
      '<div><div class="m33-pl-name m33-pl-name-lg">' + esc(p.name) + '</div><div class="m33-pl-what">' + esc(p.what) + '</div></div></div>';
    if (!p.connectable) {
      html += '<div class="m33-pl-why m33-pl-why-' + (p.availability === 'blocked' ? 'blocked' : 'soon') + '">' +
        '<b>' + (p.availability === 'blocked' ? 'Blocked' : 'Not available yet') + '.</b> ' + esc(p.reason || '') +
        (p.approver ? '<div class="m33-pl-by">Blocked by ' + esc(p.approver) + '</div>' : '') + '</div>';
    } else {
      var mine = (p.yours && p.yours.answer) || 'safe';
      html += '<fieldset class="m33-pl-q"><legend>What may ' + esc(cfg.agent) + ' do with ' + esc(p.name) + '?</legend>' +
        ['ask', 'read', 'safe'].map(function (a) {
          var hint = a === 'ask' ? 'It asks before every use.' : a === 'read' ? 'It reads on its own and asks before any change.' : 'Safe changes like drafts run; sending and deleting always ask.';
          return '<label class="m33-pl-opt"><input type="radio" name="m33-pl-ans" value="' + a + '"' + (mine === a ? ' checked' : '') + '>' +
            '<span><b>' + esc(data.answers[a]) + '</b><span class="m33-pl-hint">' + hint + '</span></span></label>';
        }).join('') + '</fieldset>' +
        (p.yours ? '' : '<button type="button" class="m33-pl-btn" id="m33-pl-add">Add ' + esc(p.name) + '</button>') +
        '<div class="m33-pl-status" id="m33-pl-status" aria-live="polite">' + esc(statusWords(p)) + '</div>' + signinBox(p);
    }
    html += '<div class="m33-pl-where">Runs: ' + esc(p.where) + '</div>';
    if (data.operator && p.signinApp) html += '<div class="m33-pl-op" id="m33-pl-app"><div class="m33-pl-sub">App for sign-in</div><div class="m33-pl-op-msg">Loading\u2026</div></div>';
    if (data.operator) html += operatorBox(p);
    body.innerHTML = html;
    document.getElementById('m33-pl-back').addEventListener('click', function () { open = null; draw(); });
    var rs = body.querySelectorAll('input[name="m33-pl-ans"]');
    // Once added, a new answer saves at once; before that, Add saves the one chosen.
    for (var i = 0; i < rs.length; i++) rs[i].addEventListener('change', function () { if (p.yours) answer(p, this.value); });
    var add = document.getElementById('m33-pl-add');
    if (add) add.addEventListener('click', function () {
      var c = body.querySelector('input[name="m33-pl-ans"]:checked');
      add.disabled = true;
      answer(p, c ? c.value : 'safe', function () { add.parentNode.removeChild(add); }, function () { add.disabled = false; });
    });
    wireOperator(p);
    wireSignin(p);
    if (data.operator && p.signinApp) loadApp(p);
  }
  // ── The app this host signs people in with (operators): the vendor's client id and secret, entered here ──
  // The secret is sealed on the server and never sent back; the box says where the app comes from and the
  // redirect URL to register in the vendor's app.
  function loadApp(p) {
    var box = document.getElementById('m33-pl-app'); if (!box) return;
    window.fetch('/api/plugins/' + encodeURIComponent(p.id) + '/app', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (a) { if (a && a.ok) drawApp(p, a); else box.querySelector('.m33-pl-op-msg').textContent = 'Could not read the app: ' + ((a && (a.message || a.error)) || 'no answer'); },
        function (e) { box.querySelector('.m33-pl-op-msg').textContent = 'Could not read the app: ' + e.message; });
  }
  function drawApp(p, a) {
    var box = document.getElementById('m33-pl-app'); if (!box) return;
    var state = a.configured
      ? 'Set (' + esc(a.source) + (a.setBy ? ', by ' + esc(a.setBy) : '') + '). Client ID ' + esc(a.clientId) + '. The secret is kept sealed and never shown.'
      : 'Not set: nobody can sign in to ' + esc(p.name) + ' here yet.';
    box.innerHTML = '<div class="m33-pl-sub">App for sign-in</div>' +
      '<div class="m33-pl-op-msg">' + state + '</div>' +
      (a.source === 'deploy config' ? '' :
        '<label class="m33-pl-op-l" for="m33-pl-app-id">Client ID</label>' +
        '<input id="m33-pl-app-id" class="m33-pl-reason" autocomplete="off" spellcheck="false" value="' + esc(a.clientId || '') + '">' +
        '<label class="m33-pl-op-l" for="m33-pl-app-secret">Client Secret' + (a.configured ? ' (enter it again to change it)' : '') + '</label>' +
        '<input id="m33-pl-app-secret" class="m33-pl-reason" type="password" autocomplete="new-password" spellcheck="false">' +
        '<div class="m33-pl-acts"><button type="button" class="m33-pl-btn" id="m33-pl-app-save">Save app</button></div>') +
      '<div class="m33-pl-note">In ' + esc(p.name) + '\u2019s app settings, the redirect URL is <code>' + esc(a.redirectUri) + '</code></div>' +
      '<div class="m33-pl-op-msg" id="m33-pl-app-msg" aria-live="polite"></div>';
    var save = document.getElementById('m33-pl-app-save');
    if (save) save.addEventListener('click', function () {
      var id = document.getElementById('m33-pl-app-id'), sec = document.getElementById('m33-pl-app-secret'), msg = document.getElementById('m33-pl-app-msg');
      if (!id.value.trim() || !sec.value.trim()) { msg.textContent = 'Enter both the Client ID and the Client Secret.'; return; }
      save.disabled = true; msg.textContent = 'Saving\u2026';
      postJSON('/api/plugins/' + encodeURIComponent(p.id) + '/app', { clientId: id.value, clientSecret: sec.value }).then(function () {
        sec.value = '';
        return load().then(function () { draw(); });
      }, function (e) { save.disabled = false; msg.textContent = 'Not saved: ' + e.message; });
    });
  }
  // ── Signing in to a vendor-hosted plugin (plugin-signin.js): the vendor's own page, in a small window ──
  function signinBox(p) {
    if (p.yours && p.yours.via === 'workspace') return '';
    if (p.auth !== 'oauth') return '<div class="m33-pl-note">Customize each tool once you have signed in.</div>';
    if (!p.signin) return p.yours ? '<div class="m33-pl-note">Signing in to ' + esc(p.name) + ' is not set up on this host yet.</div>' : '';
    if (!p.yours) return '<div class="m33-pl-note">Add it, then sign in with your own ' + esc(p.name) + ' account.</div>';
    return '<div class="m33-pl-acts">' + (p.yours.status === 'connected'
      ? '<button type="button" class="m33-pl-btn m33-pl-ghost" id="m33-pl-signout">Sign out of ' + esc(p.name) + '</button>'
      : '<button type="button" class="m33-pl-btn" id="m33-pl-signin">Sign in to ' + esc(p.name) + '</button>') + '</div>';
  }
  function wireSignin(p) {
    var a = document.getElementById('m33-pl-signin'), o = document.getElementById('m33-pl-signout'), st = document.getElementById('m33-pl-status');
    if (a) a.addEventListener('click', function () {
      var w = window.open('/api/plugins/' + encodeURIComponent(p.id) + '/signin', 'm33-plugin-signin', 'width=560,height=720');
      if (st) st.textContent = w ? 'Finish signing in to ' + p.name + ' in the window that opened.' : 'Your browser blocked the sign-in window. Allow pop-ups for this page and press Sign in again.';
    });
    if (o) o.addEventListener('click', function () {
      o.disabled = true;
      postJSON('/api/plugins/' + encodeURIComponent(p.id) + '/signin/signout', {}).then(function () { return load().then(function () { draw(); }); },
        function (e) { o.disabled = false; if (st) st.textContent = 'Not signed out: ' + e.message; });
    });
  }
  // The sign-in window says how it went, then closes; this page re-reads the plugin (and a waiting Connect card carries on).
  window.addEventListener('message', function (ev) {
    if (ev.origin !== window.location.origin || !ev.data || typeof ev.data.type !== 'string') return;
    if (ev.data.type !== 'm33-plugin-signed-in' && ev.data.type !== 'm33-plugin-signin-failed') return;
    load().then(function () { draw(); if (ev.data.type === 'm33-plugin-signed-in' && waitingConnect) checkConnect(); },
      function (e) { console.error('[plugins-view] re-reading after a sign-in failed:', e && e.message); });
  });
  function statusWords(p) {
    if (!p.yours) return 'Not added yet.';
    // Say the gap (rule 18c): connected is not yet usable — agents calling plugin tools is phase 5b.
    if (p.yours.via === 'workspace' && p.served) return 'Connected through your ' + (p.preconnect || '') + ' Google Workspace. No sign-in needed. Reading runs on its own; creating asks you first.';
    if (p.yours.via === 'workspace') return 'Linked through your ' + (p.preconnect || '') + ' Google Workspace. No sign-in needed. ' + cfg.agent + ' can\u2019t use its tools yet: that comes next.';
    return p.yours.status === 'connected' ? 'Added and ready.' : 'Added. It needs you to sign in before it can work.';
  }
  function answer(p, a, done, failed) {
    var st = document.getElementById('m33-pl-status');
    if (st) st.textContent = 'Saving…';
    postJSON('/api/plugins/' + encodeURIComponent(p.id) + '/answer', { answer: a }).then(function (d) {
      p.yours = { status: d.status, answer: d.answer, overrides: (p.yours && p.yours.overrides) || {}, via: p.yours && p.yours.via };
      if (st) st.textContent = 'Saved: ' + data.answers[d.answer] + '. ' + statusWords(p);
      if (done) done();
    }, function (e) { if (st) st.textContent = 'Not saved: ' + e.message; if (failed) failed(); });
  }

  // ── The operator: block or unblock, with a reason, asked in place ──
  function operatorBox(p) {
    var blocking = p.availability !== 'blocked';
    return '<div class="m33-pl-op"><div class="m33-pl-sub">Operator</div>' +
      (asking === p.id
        ? '<label class="m33-pl-op-l" for="m33-pl-reason">' + (blocking ? 'Why block it? People who try to add it will read this.' : 'Why unblock it?') + '</label>' +
          '<textarea id="m33-pl-reason" class="m33-pl-reason" rows="2" maxlength="400"></textarea>' +
          '<div class="m33-pl-acts"><button type="button" class="m33-pl-btn ' + (blocking ? 'm33-pl-danger' : '') + '" id="m33-pl-do">' + (blocking ? 'Block ' : 'Unblock ') + esc(p.name) + '</button>' +
          '<button type="button" class="m33-pl-btn m33-pl-ghost" id="m33-pl-cancel">Cancel</button></div>'
        : '<button type="button" class="m33-pl-btn m33-pl-ghost" id="m33-pl-ask">' + (blocking ? 'Block this plugin…' : 'Unblock this plugin…') + '</button>') +
      '<div class="m33-pl-op-msg" id="m33-pl-op-msg" aria-live="polite"></div></div>';
  }
  function wireOperator(p) {
    var a = document.getElementById('m33-pl-ask'), d = document.getElementById('m33-pl-do'), c = document.getElementById('m33-pl-cancel');
    if (a) a.addEventListener('click', function () { asking = p.id; draw(); var r = document.getElementById('m33-pl-reason'); if (r) r.focus(); });
    if (c) c.addEventListener('click', function () { asking = null; draw(); });
    if (d) d.addEventListener('click', function () {
      var reason = String(document.getElementById('m33-pl-reason').value || '').trim();
      var msg = document.getElementById('m33-pl-op-msg');
      var unblock = p.availability === 'blocked';
      if (!unblock && !reason) { msg.textContent = 'Give a reason: people who try to add it will read it.'; return; }
      d.disabled = true;
      // Unblock returns it to where the catalog started it (the server knows where that is).
      postJSON('/api/plugins/' + encodeURIComponent(p.id) + '/availability', unblock ? { unblock: true, reason: reason || undefined } : { availability: 'blocked', reason: reason }).then(function () {
        asking = null; return load().then(function () { draw(); });
      }, function (e) { d.disabled = false; msg.textContent = 'Not changed: ' + e.message; });
    });
  }

  // ── Skills: what the agent can already do itself ──
  function drawSkills() {
    h.textContent = 'Skills'; q.hidden = true;
    body.innerHTML = '<div class="m33-pl-empty">Loading…</div>';
    getJSON('/api/composer-palette').then(function (d) {
      var tools = d.tools || [];
      body.innerHTML = '<p class="m33-pl-lead">What ' + esc(cfg.agent) + ' can already do itself. Skills that come with plugins will join these.</p>' +
        '<div class="m33-pl-list">' + tools.map(function (t) {
          return '<div class="m33-pl-row"><span class="m33-pl-name">' + esc(t.name) + '</span><span class="m33-pl-what">' + esc(t.description) + '</span></div>';
        }).join('') + '</div>';
    }, function (e) { fail('load the skills', e); });
  }

  // ── Bots: the agents you can talk to ──
  // A switch flipped in Settings ▸ Agents ▸ Roster (the page's m33:tray event) is the same
  // switch as the one drawn here: each agent's own, by the tray's own rule.
  document.addEventListener('m33:tray', function (ev) {
    var p = ev && ev.detail && ev.detail.prefs, rule = window.__m33TrayRule, bots = cfg.bots || [];
    if (!p || !rule) return;
    for (var i = 0; i < bots.length; i++) if (!bots[i].place) bots[i].on = rule.elementOn('principals', bots[i].name, p, bots[i].member);
  });
  function drawBots() {
    h.textContent = 'Bots'; q.hidden = true;
    // Every Mach33 agent, each with a switch for YOUR tray when the host has one (cfg.tray: the host's
    // route and its tray rows); the place itself has none: it is always there. "Soon" marks one with no home yet.
    var bots = cfg.bots || [], tray = cfg.tray || null;
    var html = '<p class="m33-pl-lead">' + (tray ? 'Every Mach33 agent. Switch on the ones you want in your tray.' : 'The agents you can talk to here.') + '</p><div class="m33-pl-sub">Mach33’s agents</div><div class="m33-pl-grid">' +
      bots.map(function (b, i) {
        var tag = b.place ? 'The place' : b.member ? 'Member' : 'Agent';
        var sw = b.place || !tray ? '<span class="m33-pl-state m33-pl-st-on">' + (b.place ? 'Always' : 'Principal') + '</span>'
          : '<label class="m33-pl-switch" title="Show ' + esc(b.name) + ' in your tray"><input type="checkbox" data-bot="' + i + '"' + (b.on ? ' checked' : '') + '>' +
            '<span class="m33-pl-switch-track" aria-hidden="true"></span><span class="m33-pl-sr">' + esc(b.name) + ' in your tray</span></label>';
        return '<div class="m33-pl-card m33-pl-static"><span class="m33-pl-tile" aria-hidden="true">' + esc(b.name.charAt(0)) + '</span>' +
          '<span class="m33-pl-meta"><span class="m33-pl-name">' + esc(b.name) + ' <span class="m33-pl-tag">' + tag + (b.place || b.live ? '' : ' \u00b7 Soon') + '</span></span>' +
          '<span class="m33-pl-what">' + esc(b.role || '') + '</span></span>' + sw + '</div>';
      }).join('') + '</div><div class="m33-pl-status" id="m33-pl-bot-msg" aria-live="polite">' + esc((tray && tray.note) || '') + '</div><div class="m33-pl-sub">Yours</div><div id="m33-pl-workers" class="m33-pl-empty">Loading…</div>';
    body.innerHTML = html;
    var boxes = tray ? body.querySelectorAll('input[data-bot]') : [];
    for (var bi = 0; bi < boxes.length; bi++) boxes[bi].addEventListener('change', function () {
      var box = this, b = bots[+box.getAttribute('data-bot')], on = box.checked, msg = document.getElementById('m33-pl-bot-msg');
      box.disabled = true;
      postJSON(tray.route, { identity: b.name, on: on }).then(function (d) {
        b.on = on; box.disabled = false;
        // The tray row is already in the page (hidden when off): show or hide it now. The host
        // says whether it is in the tray (d.shown): an agent switched on while the whole
        // Principal Agents group is off (Settings ▸ Agents) stays out, and the line says so.
        var shown = d.shown === undefined ? on : d.shown === true;
        var rows = tray.rowSelector ? document.querySelectorAll(tray.rowSelector + '[data-identity]') : [];
        for (var r = 0; r < rows.length; r++) if (rows[r].getAttribute('data-identity') === b.name) rows[r].hidden = !shown;
        if (msg) msg.textContent = b.name + (!on ? ' is out of your tray.' : shown ? ' is in your tray.'
          : ' is switched on, but Principal Agents are switched off in Settings ▸ Agents, so it stays out of your tray.');
      }, function (e) {
        box.checked = !on; box.disabled = false;
        if (msg) msg.textContent = 'Not changed: ' + e.message;
      });
    });
    getJSON('/api/mcp/workers').then(function (d) {
      var el = document.getElementById('m33-pl-workers'); if (!el) return;
      var ws = d.workers || [];
      if (!ws.length) { el.textContent = 'No agents made for you yet. An app you connect can ask ' + cfg.agent + ' to make one.'; return; }
      el.className = 'm33-pl-grid';
      el.innerHTML = ws.map(function (w) {
        return '<div class="m33-pl-card m33-pl-static"><span class="m33-pl-tile m33-pl-tile-worker" aria-hidden="true">' + esc(w.name.charAt(0)) + '</span>' +
          '<span class="m33-pl-meta"><span class="m33-pl-name">' + esc(w.name) + '</span><span class="m33-pl-what">' + esc(w.brief || '') + '</span></span>' +
          '<span class="m33-pl-state m33-pl-st-add">Worker</span></div>';
      }).join('');
    }, function (e) { var el = document.getElementById('m33-pl-workers'); if (el) el.textContent = 'Could not load your agents: ' + e.message; });
  }

  function draw() {
    if (tab === 'skills') return drawSkills();
    if (tab === 'bots') return drawBots();
    if (!data) return;
    var p = open ? data.plugins.filter(function (x) { return x.id === open; })[0] : null;
    return p ? drawPlugin(p) : drawList();
  }
  function load() {
    return getJSON('/api/plugins').then(function (d) { data = d; }, function (e) { fail('load the plugins', e); throw e; });
  }
  function show(on) {
    root.hidden = !on;
    if (on) { tab = 'plugins'; open = null; setTab(); load().then(draw, function () {}); }
    else if (waitingConnect) checkConnect();
  }
  // Open straight onto one plugin's page (a Connect card in the conversation).
  function showPlugin(id) {
    root.hidden = false; tab = 'plugins'; open = id; asking = null; setTab(); load().then(draw, function () {});
  }
  function setTab() {
    var ts = root.querySelectorAll('.m33-pl-tab');
    for (var i = 0; i < ts.length; i++) {
      var on = ts[i].getAttribute('data-tab') === tab;
      ts[i].className = 'm33-pl-tab' + (on ? ' m33-pl-on' : '');
      ts[i].setAttribute('aria-selected', on ? 'true' : 'false');
    }
  }
  var tabs = root.querySelectorAll('.m33-pl-tab');
  for (var t = 0; t < tabs.length; t++) tabs[t].addEventListener('click', function () { tab = this.getAttribute('data-tab'); open = null; setTab(); draw(); });
  q.addEventListener('input', function () { if (tab === 'plugins' && !open && data) drawList(); });
  document.getElementById('m33-pl-x').addEventListener('click', function () { show(false); });
  root.addEventListener('click', function (e) { if (e.target === root) show(false); });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !root.hidden) show(false); });
  window.__m33OpenPlugins = function () { show(true); };

  // ── The one card in the conversation (Addendum A): allow this · a new file inline ──
  // chat-view hands every streamed "card" event here. No native dialogs.
  var TOOL_WORDS = { create_doc: 'create a Google Doc', create_presentation: 'create a Google Slides deck', create_spreadsheet: 'create a Google Sheet' };
  var KIND_OF = { doc: { path: 'document', tail: '/preview', app: 'Docs' }, slides: { path: 'presentation', tail: '/embed?start=false', app: 'Slides' }, sheet: { path: 'spreadsheets', tail: '/preview', app: 'Sheets' } };
  function el(tag, cls, text) { var n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; }
  function embedOf(e) {
    var box = el('div', 'm33-pl-embed');
    var k = KIND_OF[e.type] || KIND_OF.doc;
    var src = 'https://docs.google.com/' + k.path + '/d/' + encodeURIComponent(e.id) + k.tail;
    var f = el('iframe', 'm33-pl-embed-frame' + (e.type === 'slides' ? ' m33-pl-embed-slides' : ''));
    f.setAttribute('src', src); f.setAttribute('title', e.title || 'Google file'); f.setAttribute('loading', 'lazy');
    f.setAttribute('allowfullscreen', 'true');
    var bar = el('div', 'm33-pl-embed-bar');
    bar.appendChild(el('span', 'm33-pl-name', e.title || ''));
    var a = el('a', 'm33-pl-embed-open', 'Open in Google ' + k.app);
    a.setAttribute('href', e.url); a.setAttribute('target', '_blank'); a.setAttribute('rel', 'noopener');
    bar.appendChild(a);
    box.appendChild(bar); box.appendChild(f);
    return box;
  }
  // What the call will do, in a few lines people read (never raw JSON first).
  function argLines(c) {
    var a = c.args || {}, out = [];
    if (a.title) out.push('Title: ' + a.title);
    if (c.tool === 'create_presentation' && a.slides && a.slides.length) {
      out.push(a.slides.length + ' slides: ' + a.slides.map(function (s) { return s && s.title; }).filter(Boolean).slice(0, 6).join(' · ') + (a.slides.length > 6 ? ' …' : ''));
    } else if (c.tool === 'create_spreadsheet' && a.sheets && a.sheets.length) {
      out.push(a.sheets.map(function (t) { return (t && t.name) + ' (' + ((t && (t.rowCount || (t.rows && t.rows.length))) || 0) + ' rows)'; }).join(' \u00b7 '));
      var head = a.sheets[0] && a.sheets[0].rows && a.sheets[0].rows[0];
      if (head) out.push('Columns: ' + [].concat(head).join(' \u00b7 ').slice(0, 200));
    } else if (c.tool === 'create_doc' && a.text) {
      out.push(String(a.text).replace(/\s+/g, ' ').slice(0, 220) + (String(a.text).length > 220 ? '…' : ''));
    } else {
      Object.keys(a).forEach(function (k) { if (k !== 'title') out.push(k + ': ' + String(typeof a[k] === 'object' ? JSON.stringify(a[k]) : a[k]).slice(0, 160)); });
    }
    return out;
  }
  // Once the person has answered every open card of this reply and at least one was allowed, the agent
  // carries on with the rest of what they asked (the composer's own path), so a request with several
  // creates runs through without their retyping it. Only this reply's cards count: an older card left
  // pending (a reload, an expired approval) never holds a new request back.
  function carryOn(rec) {
    var turn = cards.filter(function (r) { return r && r.c && r.c.kind === 'allow' && r.after === rec.after; });
    if (turn.some(function (r) { return r.state && r.state.status === 'pending'; })) return;   // another card still waits for them
    if (!turn.some(function (r) { return r.state && r.state.status === 'done'; })) return;     // all denied: nothing to carry on
    resume();
  }
  // The person's go-ahead, said for them: the agent carries on with what they asked.
  function resume() {
    var chat = window.__m33chat, tries = 0;
    if (!chat || !chat.send) return;
    (function go() {
      if (chat.busy() && tries++ < 100) { window.setTimeout(go, 300); return; }
      if (!chat.send('Done \u2014 carry on with the rest of what I asked.')) console.warn('[proto:plugins:carry-on] the reply was still streaming after 30s; not carrying on by itself');
    })();
  }
  function allowCard(c, rec) {
    var card = el('div', 'm33-pl-chatcard');
    var head = el('div', 'm33-pl-chatcard-h');
    if (c.logo) { var t = el('span', 'm33-pl-tile m33-pl-tile-logo'); var im = el('img'); im.setAttribute('src', c.logo); im.setAttribute('alt', ''); t.appendChild(im); head.appendChild(t); }
    head.appendChild(el('span', 'm33-pl-chatcard-t', cfg.agent + ' wants to ' + (TOOL_WORDS[c.tool] || (c.tool || '').replace(/_/g, ' ')) + ' in your ' + (c.pluginName || c.plugin)));
    card.appendChild(head);
    argLines(c).forEach(function (l) { card.appendChild(el('div', 'm33-pl-chatcard-arg', l)); });
    var acts = el('div', 'm33-pl-acts'), st = el('div', 'm33-pl-status');
    st.setAttribute('aria-live', 'polite');
    // Answered already (a reload): its outcome, and the new file if one was made — no buttons.
    if (rec.state && rec.state.status !== 'pending') {
      st.textContent = rec.state.text || '';
      card.appendChild(st);
      if (rec.state.embed) card.appendChild(embedOf(rec.state.embed));
      return card;
    }
    function settle(status, text, embed) { rec.state = { status: status, text: text, embed: embed || null }; saveCards(); }
    [['once', 'Allow once', 'm33-pl-btn'], ['always', 'Always allow', 'm33-pl-btn m33-pl-ghost'], ['deny', 'Deny', 'm33-pl-btn m33-pl-ghost']].forEach(function (b) {
      var btn = el('button', b[2], b[1]); btn.setAttribute('type', 'button');
      btn.addEventListener('click', function () {
        var all = acts.querySelectorAll('button'); for (var i = 0; i < all.length; i++) all[i].disabled = true;
        st.textContent = b[0] === 'deny' ? 'Denying…' : 'Working…';
        postJSON('/api/effector/approvals/' + encodeURIComponent(c.id), { decision: b[0] }).then(function (d) {
          card.removeChild(acts);
          var chat = window.__m33chat;
          if (d.denied) {
            st.textContent = 'Denied. Nothing was done.';
            settle('denied', st.textContent);
            if (chat) chat.push('assistant', 'You denied it: ' + (TOOL_WORDS[c.tool] || c.tool) + (c.args && c.args.title ? ' (\u201c' + c.args.title + '\u201d)' : '') + '. Nothing was done.');
            carryOn(rec);
            return;
          }
          // A destructive tool always asks (Connection.decide): never promise otherwise.
          st.textContent = 'Done' + (d.always && c.toolKind !== 'destructive' ? ' — it will not ask again for this tool' : '') + (d.title ? ': “' + d.title + '”' : '') + '.';
          if (d.embed) card.appendChild(embedOf(d.embed));
          settle('done', st.textContent, d.embed);
          if (chat) chat.push('assistant', 'Done, on your go-ahead: ' + (d.title ? '\u201c' + d.title + '\u201d in ' : '') + (c.pluginName || c.plugin) + (d.url ? ' \u2014 ' + d.url : '') + '.');
          carryOn(rec);   // after the note above, so the carried-on turn's history already holds the new file
        }, function (e) {
          // An approval lives on the server for a few minutes; after that (or a restart) it is gone.
          if (/no pending approval|expired/.test(e.message)) {
            card.removeChild(acts);
            st.textContent = 'This request expired. Nothing was done \u2014 ask ' + cfg.agent + ' again.';
            settle('expired', st.textContent);
            return;
          }
          st.textContent = 'Not done: ' + e.message;
          for (var j = 0; j < all.length; j++) all[j].disabled = false;
        });
      });
      acts.appendChild(btn);
    });
    card.appendChild(acts); card.appendChild(st);
    return card;
  }
  // Cards are kept beside the conversation's own history in this browser (chat-view keeps the
  // text there), each with its place: after the reply it arrived in, and its outcome so far.
  var chatRoot = document.querySelector('.m33-chat[data-agent]');
  var CARD_KEY = 'm33proto:chat:' + ((chatRoot && chatRoot.getAttribute('data-agent')) || 'agent') + ':cards';
  var cards = [];
  try { cards = JSON.parse(window.localStorage.getItem(CARD_KEY) || '[]') || []; } catch (_) { cards = []; }
  function saveCards() {
    var keep = cards.slice(-50).map(function (r) { return { c: Object.assign({}, r.c, { args: compactArgs(r.c.args) }), after: r.after, state: r.state }; });
    try { window.localStorage.setItem(CARD_KEY, JSON.stringify(keep)); } catch (e) { console.warn('[proto:plugins:cards] could not keep the conversation\u2019s cards in this browser:', e && e.message); }
  }
  // What a saved card keeps of the call's arguments: enough to redraw it, never a whole Sheet (storage is small).
  function compactArgs(a) {
    a = a || {};
    var o = {};
    Object.keys(a).forEach(function (k) {
      var v = a[k];
      if (k === 'sheets' && Array.isArray(v)) o.sheets = v.slice(0, 10).map(function (t) { return { name: t && t.name, rows: ((t && t.rows) || []).slice(0, 1), rowCount: ((t && t.rows) || []).length }; });
      else if (k === 'slides' && Array.isArray(v)) o.slides = v.slice(0, 30).map(function (x) { return { title: x && x.title }; });
      else if (typeof v === 'string') o[k] = v.slice(0, 400);
      else if (v == null || typeof v !== 'object') o[k] = v;
    });
    return o;
  }
  // Not connected (ADR 0086 D7): the agent starts the connection in place. This card opens the plugin's
  // page in Plugins; when the person closes Plugins with it connected, the agent carries on with the
  // request without their asking again. They connect it themselves — no password passes through the chat.
  var waitingConnect = null;
  function connectCard(c, rec) {
    var card = el('div', 'm33-pl-chatcard');
    var head = el('div', 'm33-pl-chatcard-h');
    head.appendChild(el('span', 'm33-pl-chatcard-t', cfg.agent + ' needs your ' + (c.name || c.plugin) + ' connected to ' + (TOOL_WORDS[c.tool] || 'do this') + '.'));
    card.appendChild(head);
    var acts = el('div', 'm33-pl-acts'), st = el('div', 'm33-pl-status');
    st.setAttribute('aria-live', 'polite');
    if (rec.state && rec.state.status !== 'pending') { st.textContent = rec.state.text || ''; card.appendChild(st); return card; }
    var btn = el('button', 'm33-pl-btn', 'Connect ' + (c.name || c.plugin)); btn.setAttribute('type', 'button');
    btn.addEventListener('click', function () { waitingConnect = { rec: rec, card: card, acts: acts, st: st }; st.textContent = 'Connect it in Plugins, then close Plugins.'; showPlugin(c.plugin); });
    acts.appendChild(btn); card.appendChild(acts); card.appendChild(st);
    return card;
  }
  function checkConnect() {
    var w = waitingConnect; waitingConnect = null;
    load().then(function () {
      var p = data && data.plugins.filter(function (x) { return x.id === w.rec.c.plugin; })[0];
      if (p && p.yours && p.yours.status === 'connected') {
        if (w.acts.parentNode) w.card.removeChild(w.acts);
        w.st.textContent = (p.name || w.rec.c.plugin) + ' is connected. Carrying on\u2026';
        w.rec.state = { status: 'done', text: (p.name || w.rec.c.plugin) + ' connected.' }; saveCards();
        resume();
      } else {
        w.st.textContent = 'Not connected yet. Press Connect when you are ready.';
      }
    }, function (e) { w.st.textContent = 'Could not check the connection: ' + ((e && e.message) || e); });
  }
  function nodeOf(rec) { return rec.c.kind === 'allow' ? allowCard(rec.c, rec) : rec.c.kind === 'embed' ? embedOf(rec.c) : rec.c.kind === 'connect' ? connectCard(rec.c, rec) : null; }
  window.__m33ChatCard = function (c) {
    var scroll = document.getElementById('m33-chat-scroll');
    if (!scroll || !c || !c.kind) return;
    // The reply this card belongs to is still streaming: it becomes history item N.
    var rec = { c: c, after: window.__m33chat ? window.__m33chat.history().length : 0, state: { status: c.kind === 'embed' ? 'done' : 'pending' } };
    var node = nodeOf(rec);
    if (!node) return;
    cards.push(rec); saveCards();
    scroll.appendChild(node);
    scroll.scrollTop = scroll.scrollHeight;
  };
  // On load: put each saved card back after its reply, once chat-view has drawn the history.
  (function restore(tries) {
    var scroll = document.getElementById('m33-chat-scroll');
    if (!scroll || !window.__m33chat) { if (tries < 40) window.setTimeout(function () { restore(tries + 1); }, 100); return; }
    var n = window.__m33chat.history().length;
    if (!n) { if (cards.length) { cards = []; saveCards(); } return; }   // a new conversation: its old cards go too
    var msgs = scroll.querySelectorAll('.m33-chat-msg');
    if (msgs.length < Math.min(n, 1) && tries < 40) { window.setTimeout(function () { restore(tries + 1); }, 100); return; }
    var lastAfter = {};
    cards = cards.filter(function (r) { return r && r.c && r.after <= n; });
    cards.forEach(function (r) {
      var node = nodeOf(r); if (!node) return;
      var anchor = lastAfter[r.after] || msgs[r.after] || null;
      if (anchor && anchor.parentNode === scroll) scroll.insertBefore(node, anchor.nextSibling); else scroll.appendChild(node);
      lastAfter[r.after] = node;
    });
    scroll.scrollTop = scroll.scrollHeight;
  })(0);
}
